import { ImageQuality } from "@/store/slices/aiFashion/types";

export type GeminiPromptText = string | { prompt: number }
export type GeminiInlineDataPart = { inlineData: { data: string; mimeType: string } }
export type GeminiTextPart = { text: GeminiPromptText }
export type GeminiPart = GeminiTextPart | GeminiInlineDataPart
export type GeminiContent = { parts?: Array<{ text?: string }> }
export type GeminiGenerateParams = {
  contents: Array<{ parts: GeminiPart[]; role: 'user' }>
  generationConfig: {
    temperature: number
    topP: number
    topK: number
    responseModalities: string[]
    imageConfig?: {
      aspectRatio?: string
      imageSize?: string
    }
  }
}

type ParseDataUrlResult = { mimeType: string; data: string }
type ParseDataUrlFn = (dataUrl: string) => ParseDataUrlResult

export function buildGeminiParts(
  prompt: GeminiPromptText,
  base64image: string | string[],
  parseDataUrl: ParseDataUrlFn
): GeminiPart[] {
  const parts: GeminiPart[] = [{ text: prompt }]

  if (Array.isArray(base64image)) {
    base64image.forEach((imageData) => {
      const parsedImageData = parseDataUrl(imageData)
      parts.push({
        inlineData: {
          data: parsedImageData.data,
          mimeType: parsedImageData.mimeType
        }
      })
    })
    return parts
  }

  const parsedImageData = parseDataUrl(base64image)
  parts.push({
    inlineData: {
      data: parsedImageData.data,
      mimeType: parsedImageData.mimeType
    }
  })
  return parts
}

export function buildGeminiGenerationConfig(
  responseModalities: string[] = ['Text', 'Image'],
  aspectRatio?: string,
  imageSize?: string
): GeminiGenerateParams['generationConfig'] {
  const config: GeminiGenerateParams['generationConfig'] = {
    temperature: 0.1,
    topP: 0.8,
    topK: 20,
    responseModalities,
  }
  if (aspectRatio || imageSize) {
    config.imageConfig = {
      ...(aspectRatio ? { aspectRatio } : {}),
      ...(imageSize ? { imageSize } : {}),
    }
  }
  return config
}

const GEMINI_SUPPORTED_RATIOS: [number, string][] = [
  [1.0, '1:1'],
  [2 / 3, '2:3'],
  [3 / 2, '3:2'],
  [3 / 4, '3:4'],
  [4 / 3, '4:3'],
  [4 / 5, '4:5'],
  [5 / 4, '5:4'],
  [9 / 16, '9:16'],
  [16 / 9, '16:9'],
]

export function geminiAspectRatioString(aspectRatio: number | undefined | null): string | undefined {
  if (!aspectRatio || aspectRatio <= 0) return undefined
  const tolerance = 0.05
  let closest: [number, string] | undefined
  let closestDiff = Infinity
  for (const [value, label] of GEMINI_SUPPORTED_RATIOS) {
    const diff = Math.abs(value - aspectRatio)
    if (diff < closestDiff) {
      closestDiff = diff
      closest = [value, label]
    }
  }
  if (!closest || closestDiff > tolerance) return undefined
  return closest[1]
}

export function geminiImageSizeString(resolution?: ImageQuality): string | undefined {
  if (resolution === 'standard') {
    return '1K'
  } else if (resolution === 'hd') {
    return '2K'
  }
  else if (resolution === '4k') {
    return '4K'
  }
  else if (resolution === 'low') {
    return '512'
  }
  return '1K'
}


export function extractGeminiContentFromApiResponse(data: any): GeminiContent | undefined {
  return data?.result || data?.content || data?.candidates?.[0]?.content || data?.data?.result
}

export function extractGeminiText(content: any): string {
  if (!content?.parts || !Array.isArray(content.parts)) return ''

  const textPart = [...content.parts].reverse().find((part: any) => typeof part?.text === 'string')
  return textPart?.text || ''
}
